import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, User, Trash2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { useT } from "@/context/LanguageContext";

const HISTORY_KEY = "weddly_ai_history";
const MAX_MESSAGES = 50;
const MAX_CHARS = 4000;

const STARTERS = {
  en: [
    "Help me draft a detailed rundown for Akad and evening Reception (500 guests).",
    "Split a Rp 300 million budget for a wedding in Jakarta.",
    "Draft a family welcome speech for a Sangjit ceremony.",
    "Eco-friendly wedding souvenir ideas under Rp 25,000 per piece.",
  ],
  id: [
    "Bantu susun rundown detail untuk prosesi Akad Nikah dan Resepsi malam (500 tamu).",
    "Hitung proporsi alokasi budget Rp 300 juta untuk pernikahan di Jakarta.",
    "Buatkan draf teks sambutan keluarga untuk prosesi Sangjit.",
    "Ide souvenir pernikahan ramah lingkungan di bawah Rp 25.000 per pcs.",
  ],
};

function parseInlineMarkdown(text) {
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*(.*?)\*\*|\*([^*]+)\*|`(.*?)`)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    if (match[2] !== undefined) {
      parts.push(<strong key={key++} className="font-bold text-foreground">{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={key++} className="italic">{match[3]}</em>);
    } else if (match[4] !== undefined) {
      parts.push(<code key={key++} className="bg-muted-foreground/20 px-1 py-0.5 rounded text-xs font-mono">{match[4]}</code>);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

function FormattedMessage({ content }) {
  if (!content) return null;

  const lines = content.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Format tabel Markdown (| Header | Header |)
    if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|") && lines[i].trim().endsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      
      if (tableLines.length >= 2) {
        const headerRow = tableLines[0]
          .split("|")
          .slice(1, -1)
          .map((c) => c.trim());
        
        let startIndex = 1;
        if (tableLines[1] && tableLines[1].includes("---")) {
          startIndex = 2;
        }

        const bodyRows = tableLines.slice(startIndex).map((rowStr) =>
          rowStr
            .split("|")
            .slice(1, -1)
            .map((c) => c.trim())
        );

        elements.push(
          <div key={`table_${i}`} className="my-3 overflow-x-auto rounded-xl border border-border/80 bg-background/90 shadow-xs">
            <table className="w-full text-xs text-left border-collapse">
              <thead className="bg-primary/10 text-primary uppercase text-[11px] font-bold border-b border-border/70">
                <tr>
                  {headerRow.map((col, idx) => (
                    <th key={idx} className="px-3.5 py-2.5 whitespace-nowrap">
                      {parseInlineMarkdown(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {bodyRows.map((row, rIdx) => (
                  <tr key={rIdx} className="hover:bg-muted/50 transition-colors">
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="px-3.5 py-2.5 text-foreground leading-snug">
                        {parseInlineMarkdown(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Format Judul/Heading
    if (line.startsWith("### ")) {
      elements.push(
        <h3 key={i} className="font-serif font-bold text-base mt-3 mb-1 text-foreground">
          {parseInlineMarkdown(line.slice(4))}
        </h3>
      );
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="font-serif font-bold text-lg mt-4 mb-1 text-foreground border-b border-border/40 pb-1">
          {parseInlineMarkdown(line.slice(3))}
        </h2>
      );
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      elements.push(
        <h1 key={i} className="font-serif font-bold text-xl mt-4 mb-2 text-foreground border-b border-border pb-1">
          {parseInlineMarkdown(line.slice(2))}
        </h1>
      );
      i++;
      continue;
    }

    // Format Poin/List (- or * or digits.)
    const listMatch = line.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
    if (listMatch) {
      const indent = listMatch[1].length;
      const contentText = listMatch[3];
      elements.push(
        <div key={i} className={`flex items-start gap-2 my-1 text-sm leading-relaxed ${indent > 0 ? "ml-4" : ""}`}>
          <span className="text-primary font-bold shrink-0 mt-0.5">•</span>
          <div>{parseInlineMarkdown(contentText)}</div>
        </div>
      );
      i++;
      continue;
    }

    // Baris kosong
    if (!line.trim()) {
      elements.push(<div key={i} className="h-1.5" />);
      i++;
      continue;
    }

    // Paragraf biasa
    elements.push(
      <p key={i} className="text-sm leading-relaxed my-0.5">
        {parseInlineMarkdown(line)}
      </p>
    );
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

export default function AIChat() {
  const { t, lang } = useT();
  // Hydrasi saat render pertama (bukan useEffect) supaya tidak ada frame kosong
  // setelah refresh. Data bisa rusak dari versi lama / suntikan manual, jadi difilter.
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((m) => m && m.id && m.role) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const endRef = useRef(null);

  // Hanya dipanggil di titik henti (bukan tiap chunk), karena setMessages berjalan
  // ratusan kali per detik saat streaming dan localStorage.write itu blocking.
  const persist = (list) => {
    try {
      const trimmed = list
        .slice(-MAX_MESSAGES)
        .map((m) => ({ ...m, content: (m.content || "").slice(0, MAX_CHARS) }));
      localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
    } catch {}
  };

  const clearHistory = () => {
    setMessages([]);
    setConfirmClear(false);
    try { localStorage.removeItem(HISTORY_KEY); } catch {}
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setConfirmClear(false);
    const userMsgId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const botMsgId = `bot_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    setMessages((m) => {
      const next = [...m, { id: userMsgId, role: "user", content: msg }, { id: botMsgId, role: "assistant", content: "" }];
      persist(next);
      return next;
    });
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, lang }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + chunk };
          return copy;
        });
        endRef.current?.scrollIntoView({ behavior: "smooth" });
      }
      setMessages((m) => { persist(m); return m; });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, content: t("ai.error") };
        persist(copy);
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10 flex flex-col h-[calc(100vh-2rem)]">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl flex items-center gap-2"><Sparkles className="h-7 w-7 text-primary" /> {t("ai.title")}</h1>
        </div>
        {messages.length > 0 && !busy && (
          confirmClear ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t("ai.clear_confirm")}</span>
              <Button data-testid="ai-clear-history-confirm" variant="destructive" size="sm" onClick={clearHistory}>
                {t("ai.clear_yes")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmClear(false)}>
                {t("ai.clear_no")}
              </Button>
            </div>
          ) : (
            <Button data-testid="ai-clear-history" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setConfirmClear(true)}>
              <Trash2 /> {t("ai.clear")}
            </Button>
          )
        )}
      </div>

      <Card className="flex-1 overflow-hidden rounded-2xl flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              <Sparkles className="h-8 w-8 mx-auto mb-3 text-primary" />
              <p className="mb-6">{t("ai.intro")}</p>
              <div className="grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto text-left">
                {(STARTERS[lang] || STARTERS.en).map((s) => (
                  <button data-testid="ai-prompt-chip" key={s} onClick={() => send(s)} className="rounded-xl border border-border bg-card p-3 text-sm hover:shadow-md transition-shadow duration-200">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4 text-primary" /></div>}
              <div className={`rounded-2xl px-4 py-3 max-w-[85%] text-sm ${m.role === "user" ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-muted"}`}>
                {m.role === "user" ? (
                  m.content
                ) : m.content ? (
                  <FormattedMessage content={m.content} />
                ) : busy ? (
                  "…"
                ) : (
                  ""
                )}
              </div>
              {m.role === "user" && <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-3 flex items-end gap-2">
          <Textarea
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t("ai.placeholder")}
            className="min-h-[48px] max-h-40 resize-none"
          />
          <Button data-testid="ai-send-message-btn" onClick={() => send()} disabled={busy || !input.trim()} className="rounded-full h-12 w-12 p-0"><Send className="h-4 w-4" /></Button>
        </div>
      </Card>
    </div>
  );
}
